/**
 * /tools/client-commitment?clientId=X — F-022 (2026-05-23).
 *
 * Client Commitment Agreement. The full prototype copy (4 commitments +
 * phase-gate note + what-you-receive + coach commitment) rendered with
 * brand tokens, plus a signature area that pre-fills:
 *   - Client name from the auth user (editable for typo fixes)
 *   - Program Start Date from `clients.coaching_start_date` (or
 *     `program_start_date` fallback) — READ-ONLY for the client per
 *     Michael's directive.
 *   - Coach name — display only (read-only, captured at signing time
 *     for audit fidelity).
 *
 * Submits via the generic tools route → persists to `client_documents`
 * with `doc_type='commitment_agreement'`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { usePristine } from '@/lib/use-pristine';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface FormState {
  clientNameSigned: string;
  clientSignatureText: string;
}

/** The captured snapshot we render in the post-signing read-only view. */
interface SignedRecord {
  clientNameSigned: string;
  clientSignatureText: string;
  programStartDateIso: string;
  dateSignedIso: string;
  coachName: string;
  agreementVersion: string;
}

const AGREEMENT_VERSION = '2026-05-23-v1';
const EMPTY_FORM: FormState = { clientNameSigned: '', clientSignatureText: '' };

export function ClientCommitmentPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [programStartDateIso, setProgramStartDateIso] = useState<string | null>(null);
  const [coachName] = useState<string>('Michael Hildebrandt'); // F-022 placeholder; coach-self endpoint coming later
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [signedRecord, setSignedRecord] = useState<SignedRecord | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [settingStartDate, setSettingStartDate] = useState(false);

  // Client set-once: when no program start date exists yet, let the client
  // anchor it to today. The server enforces set-once for clients (a coach-
  // entered or Step-1-session-auto date locks it).
  const onSetStartToday = async (): Promise<void> => {
    if (!clientId) return;
    setSettingStartDate(true);
    setSubmitError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const saved = await api.setProgramStartDate(accessToken, clientId, today);
      setProgramStartDateIso(saved);
    } catch (cause) {
      setSubmitError(
        cause instanceof ApiError
          ? cause.envelope.message
          : t('tools.commitment.errors.setStartFailed'),
      );
    } finally {
      setSettingStartDate(false);
    }
  };
  // Mirrors the F-022 KPI form: button disabled "Signed ✓" until dirty,
  // then enabled "Sign agreement" — post-submit the page locks to the
  // read-only signed-record view, so the button state is only visible
  // pre-signature.
  const { isPristine, markSaved } = usePristine<FormState>(EMPTY_FORM);

  /* Hydrate: prefill name from auth user, fetch program start date from
   * the client's record, and load any prior signing (display-only). */
  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        // Pre-fill client name from the auth user (editable for typo
        // fix). Signature stays EMPTY — the client must type it
        // themselves (Michael 2026-05-23, electronic-signature intent).
        if (user) {
          setForm((prev) => ({
            ...prev,
            clientNameSigned: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          }));
        }
        // Pull the client row for the locked program start date.
        // Michael 2026-05-23 — use program_start_date as the sole source
        // (coaching_start_date is being deprecated UI-side).
        try {
          const self = await api.getClientSelf(accessToken, clientId);
          if (active) setProgramStartDateIso(self.program_start_date ?? null);
        } catch {
          /* Soft fail — the date input shows "not yet set" and the
             submit gate flags missing date. */
        }
        // Any prior signed copy → lock the page to the signed-record
        // view (mirrors the KPI baseline lockout).
        try {
          const existing = await api.getToolCompletion(
            accessToken,
            clientId,
            'client-commitment',
          );
          if (existing?.completed_at && existing.payload && typeof existing.payload === 'object') {
            const p = existing.payload as Partial<SignedRecord> & Record<string, unknown>;
            setSignedRecord({
              clientNameSigned: typeof p.clientNameSigned === 'string' ? p.clientNameSigned : '',
              clientSignatureText:
                typeof p.clientSignatureText === 'string' ? p.clientSignatureText : '',
              programStartDateIso:
                typeof p.programStartDateIso === 'string' ? p.programStartDateIso : '',
              dateSignedIso:
                typeof p.dateSignedIso === 'string' ? p.dateSignedIso : existing.completed_at.slice(0, 10),
              coachName: typeof p.coachName === 'string' ? p.coachName : coachName,
              agreementVersion:
                typeof p.agreementVersion === 'string' ? p.agreementVersion : AGREEMENT_VERSION,
            });
          }
        } catch {
          /* not signed yet */
        }
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, user, coachName]);

  const dirty = !isPristine(form);
  const hasRequired =
    form.clientNameSigned.trim().length >= 1 &&
    form.clientSignatureText.trim().length >= 1 &&
    programStartDateIso !== null;
  const canSubmit = useMemo(
    () => !submitting && hydrated && hasRequired && dirty,
    [submitting, hydrated, hasRequired, dirty],
  );

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.commitment.errors.missingClient'));
      return;
    }
    if (!programStartDateIso) {
      setSubmitError(t('tools.commitment.errors.noStartDate'));
      return;
    }
    setSubmitting(true);
    const todayIso = new Date().toISOString().slice(0, 10);
    const payload = {
      clientNameSigned: form.clientNameSigned.trim(),
      clientSignatureText: form.clientSignatureText.trim(),
      programStartDateIso,
      dateSignedIso: todayIso,
      coachName,
      agreementVersion: AGREEMENT_VERSION,
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'client-commitment',
        { payload },
      );
      // Lock the page to the signed-record view immediately.
      setSignedRecord({
        clientNameSigned: payload.clientNameSigned,
        clientSignatureText: payload.clientSignatureText,
        programStartDateIso: payload.programStartDateIso,
        dateSignedIso: payload.dateSignedIso,
        coachName: payload.coachName,
        agreementVersion: payload.agreementVersion,
      });
      markSaved(form);
      void completion; // satisfied: signedRecord already carries dateSignedIso
    } catch (cause) {
      setSubmitError(
        cause instanceof ApiError
          ? cause.envelope.message || t('tools.commitment.errors.generic')
          : t('tools.commitment.errors.generic'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="commitment-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.commitment.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.commitment.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7" data-testid="commitment">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6 rounded-xl bg-ink text-bone p-s6 text-center">
        <p className="font-mono text-xs uppercase tracking-wider text-flame-glow">
          {t('tools.commitment.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-flame mt-s2">
          {t('tools.commitment.title')}
        </h1>
        <p className="mt-s2 text-sm text-bone/70">{t('tools.commitment.subtitle')}</p>
      </header>

      {signedRecord && (
        <article
          className="mb-s5 rounded-md border border-flame/50 bg-flame-glow/30 p-s4"
          role="status"
          data-testid="commitment-already-signed"
        >
          <p className="text-sm text-ink font-medium">
            ✓ {t('tools.commitment.alreadySigned', { when: signedRecord.dateSignedIso })}
          </p>
          <p className="mt-s1 text-xs text-ink/80">{t('tools.commitment.locked')}</p>
        </article>
      )}

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s6"
        noValidate
      >
        <section className="space-y-s4">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.commitment.whatYoureSigningUpFor.title')}
          </h2>
          <CommitmentBox
            number={1}
            titleKey="tools.commitment.time.title"
            bodyKey="tools.commitment.time.body"
            detailKey="tools.commitment.time.detail"
          />
          <CommitmentBox
            number={2}
            titleKey="tools.commitment.tracking.title"
            bodyKey="tools.commitment.tracking.body"
            detailKey="tools.commitment.tracking.detail"
          />
          <CommitmentBox
            number={3}
            titleKey="tools.commitment.fieldWork.title"
            bodyKey="tools.commitment.fieldWork.body"
            detailKey="tools.commitment.fieldWork.detail"
          />
          <CommitmentBox
            number={4}
            titleKey="tools.commitment.honesty.title"
            bodyKey="tools.commitment.honesty.body"
            detailKey="tools.commitment.honesty.detail"
          />
        </section>

        <section className="space-y-s4">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.commitment.whatToKnow.title')}
          </h2>
          <article className="rounded-md border-l-4 border-flame-deep bg-flame-glow/20 p-s4">
            <h3 className="font-display text-lg text-flame-deep">
              {t('tools.commitment.phaseGates.title')}
            </h3>
            <p className="mt-s2 text-sm text-ink">{t('tools.commitment.phaseGates.body')}</p>
          </article>
          <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s4">
            <h3 className="font-display text-lg text-flame">
              {t('tools.commitment.whatYouReceive.title')}
            </h3>
            <p className="mt-s2 text-sm text-ink">{t('tools.commitment.whatYouReceive.body')}</p>
            <ul className="mt-s3 list-disc pl-s5 space-y-s1 text-sm text-ink/90">
              <li>{t('tools.commitment.whatYouReceive.li.sessions')}</li>
              <li>{t('tools.commitment.whatYouReceive.li.hos')}</li>
              <li>{t('tools.commitment.whatYouReceive.li.heroJourney')}</li>
              <li>{t('tools.commitment.whatYouReceive.li.tools')}</li>
              <li>{t('tools.commitment.whatYouReceive.li.dataProof')}</li>
              <li>{t('tools.commitment.whatYouReceive.li.checkin90')}</li>
            </ul>
          </article>
        </section>

        <section className="space-y-s4">
          <h2 className="font-display text-display-md text-ink border-b-2 border-flame pb-s2">
            {t('tools.commitment.coachCommitment.title')}
          </h2>
          <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s4">
            <p className="text-sm text-ink italic">
              {t('tools.commitment.coachCommitment.body')}
            </p>
          </article>
        </section>

        {signedRecord ? (
          /* Post-sign read-only record. Mirrors the KPI baseline
             lockout — the agreement reference text above stays
             visible for re-reading, but the signature area is now a
             captured record with no editable inputs and no submit. */
          <section
            className="rounded-md border-2 border-flame bg-bone p-s6 space-y-s4"
            data-testid="commitment-signed-record"
          >
            <h3 className="font-display text-display-md text-ink">
              {t('tools.commitment.signedRecord.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-s4">
              <div>
                <label className="form-label">{t('tools.commitment.fields.clientName')}</label>
                <p
                  className="mt-s1 text-ink font-medium"
                  data-testid="commitment-record-name"
                >
                  {signedRecord.clientNameSigned}
                </p>
              </div>
              <div>
                <label className="form-label">{t('tools.commitment.fields.dateSigned')}</label>
                <p
                  className="mt-s1 text-ink font-medium"
                  data-testid="commitment-record-date"
                >
                  {signedRecord.dateSignedIso}
                </p>
              </div>
            </div>
            <div>
              <label className="form-label">{t('tools.commitment.fields.signature')}</label>
              <p
                className="mt-s2 font-display text-2xl text-flame-deep italic"
                data-testid="commitment-record-signature"
              >
                {signedRecord.clientSignatureText}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-s4 pt-s3 border-t border-bone-3">
              <div>
                <label className="form-label">{t('tools.commitment.fields.coachName')}</label>
                <p className="mt-s1 text-ink font-medium">{signedRecord.coachName}</p>
              </div>
              <div>
                <label className="form-label">
                  {t('tools.commitment.fields.programStartDate')}
                </label>
                <p className="mt-s1 text-ink font-medium">{signedRecord.programStartDateIso}</p>
              </div>
            </div>
            <p className="text-xs text-gray-1 pt-s2 border-t border-bone-3">
              {t('tools.commitment.signedRecord.versionLabel')} {signedRecord.agreementVersion}
            </p>
          </section>
        ) : (
          <>
            <section
              className="rounded-md border-2 border-dashed border-flame bg-bone p-s6 space-y-s4"
              data-testid="commitment-signature-area"
            >
              <h3 className="font-display text-display-md text-ink">
                {t('tools.commitment.agreement.title')}
              </h3>
              <p className="text-sm text-ink/80">{t('tools.commitment.agreement.body')}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-s4">
                <div>
                  <label className="form-label" htmlFor="commitment-name">
                    {t('tools.commitment.fields.clientName')}
                  </label>
                  <input
                    id="commitment-name"
                    type="text"
                    value={form.clientNameSigned}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, clientNameSigned: e.target.value }))
                    }
                    className="input-field mt-s1"
                    data-testid="commitment-client-name"
                    maxLength={160}
                    required
                  />
                </div>
                <div>
                  <label className="form-label" htmlFor="commitment-date-signed">
                    {t('tools.commitment.fields.dateSigned')}
                  </label>
                  <input
                    id="commitment-date-signed"
                    type="text"
                    value={new Date().toISOString().slice(0, 10)}
                    readOnly
                    className="input-field mt-s1 bg-bone-2 cursor-default"
                    data-testid="commitment-date-signed"
                  />
                </div>
              </div>

              <div>
                <label className="form-label" htmlFor="commitment-signature">
                  {t('tools.commitment.fields.signature')}
                </label>
                <input
                  id="commitment-signature"
                  type="text"
                  value={form.clientSignatureText}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, clientSignatureText: e.target.value }))
                  }
                  className="input-field mt-s1 font-display text-lg"
                  placeholder={t('tools.commitment.fields.signaturePlaceholder')}
                  data-testid="commitment-signature"
                  maxLength={200}
                  required
                />
                <p className="mt-s1 text-xs text-gray-1">
                  {t('tools.commitment.fields.signatureHint')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-s4 pt-s3 border-t border-bone-3">
                <div>
                  <label className="form-label">{t('tools.commitment.fields.coachName')}</label>
                  <p
                    className="mt-s1 text-ink font-medium"
                    data-testid="commitment-coach-name"
                  >
                    {coachName}
                  </p>
                </div>
                <div>
                  <label className="form-label">
                    {t('tools.commitment.fields.programStartDate')}
                  </label>
                  {programStartDateIso ? (
                    <p
                      className="mt-s1 text-ink font-medium"
                      data-testid="commitment-program-start"
                    >
                      {programStartDateIso}
                    </p>
                  ) : (
                    <div className="mt-s1" data-testid="commitment-program-start">
                      <p className="text-sm text-flame-ember">
                        {t('tools.commitment.fields.programStartDateMissing')}
                      </p>
                      <button
                        type="button"
                        onClick={() => void onSetStartToday()}
                        disabled={settingStartDate}
                        className="btn-primary mt-s2 text-sm disabled:opacity-50"
                        data-testid="commitment-set-start-today"
                      >
                        {settingStartDate
                          ? t('common.actions.saving')
                          : t('tools.commitment.fields.setStartToday')}
                      </button>
                    </div>
                  )}
                  <p className="mt-s1 text-xs text-gray-1">
                    {t('tools.commitment.fields.programStartDateHint')}
                  </p>
                </div>
              </div>
            </section>

            {submitError && (
              <p role="alert" className="form-error" data-testid="commitment-submit-error">
                {submitError}
              </p>
            )}

            <button
              type="submit"
              className={
                canSubmit
                  ? 'btn-primary w-full'
                  : 'btn-primary w-full opacity-50 cursor-not-allowed'
              }
              disabled={!canSubmit}
              data-testid="commitment-submit"
            >
              {submitting
                ? t('tools.commitment.submitting')
                : dirty
                  ? t('tools.commitment.submit')
                  : t('tools.commitment.saved')}
            </button>
          </>
        )}
        <FinalizeWarning />
      </form>
    </section>
  );
}

interface CommitmentBoxProps {
  number: number;
  titleKey: string;
  bodyKey: string;
  detailKey: string;
}

function CommitmentBox({ number, titleKey, bodyKey, detailKey }: CommitmentBoxProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <article className="rounded-md border-l-4 border-flame bg-bone-2 p-s4 shadow-sm">
      <h3 className="font-display text-lg text-ink">
        {number}. {t(titleKey)}
      </h3>
      <p className="mt-s2 text-sm text-ink">{t(bodyKey)}</p>
      <p className="mt-s3 rounded-md bg-flame-glow/30 p-s3 text-xs text-ink/90 leading-relaxed">
        {t(detailKey)}
      </p>
    </article>
  );
}
